import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { IRule } from '@/classes/rules/IRule';
import { DefaultValue } from '@/classes/rules/DefaultValue';
import { Invalidation } from '@/classes/rules/Invalidation';
import { FieldUsage } from '@/classes/rules/FieldUsage';

const noDefaultValue = () => null;
const available = () => true;

/**
 * Generic IRule that takes as param an array of options of the specified type T,
 * and optionaly a comparison function.
 * The checkValue function checks that the value is equal to one of the options,
 * according to the comparison function.
 */
export class OptionsRule<T, Context = ScenarioExt> implements IRule<Context> {

  public key: string;
  public options: T[];
  public required: boolean = true;
  public displayed: boolean = true;
  public fieldUsage: FieldUsage;
  public getDefaultValue: (context?: Context) => DefaultValue<T> | null;
  public isAvailable: (context: Context) => boolean;

  public constructor(
    key: string,
    options: T[],
    isAvailable?: (context: Context) => boolean,
    getDefaultValue?: (context?: Context) => DefaultValue<T> | null,
    required?: boolean
  ) {
    this.key = key;
    this.options = options;
    this.isAvailable = isAvailable ?? available;
    this.getDefaultValue = getDefaultValue ?? noDefaultValue;
    if (required === false || required === true) {
      this.required = required;
    }
    this.fieldUsage = new FieldUsage();
  }

  public get invalidation(): Invalidation {
    return this.fieldUsage.getInvalidation();
  }

  public checkValue(val: T | null, context: Context): boolean | null {
    if (val === null) {
      return null;
    }

    return this.options.find(option => option === val) !== undefined;
  }

  public withIsAvailable(isAvailable: (context: Context) => boolean): this {
    this.isAvailable = isAvailable;
    return this;
  }

  public withDefaultValue(getDefaultValue: (context?: Context) => DefaultValue<T> | null): this {
    this.getDefaultValue = getDefaultValue;
    return this;
  }

  public withOptions(optArr: T[]): this {
    this.options = optArr;
    return this;
  }

  public withRequired(isRequired?: boolean): this { // true is default setting for this function
    this.required = isRequired !== false;
    return this;
  }

  public withFieldUsage(fieldUsage: FieldUsage): this {
    this.fieldUsage = fieldUsage;
    return this;
  }
}
